import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import {
  formatDateTime,
  formatMoney,
  isRedundantAddressNote,
  t,
  tr,
  type MessageKey,
} from '@aquabox/shared';
import { ApiError, api } from '../api/client';
import {
  useHayatPushAttempts,
  useHayatPushMutation,
  type HayatPushAction,
  useMe,
  useOrderMutations,
  useReverseExternalLedger,
  useTenant,
  useVehicles,
} from '../api/queries';
import { ConfirmDialog } from '../components/ConfirmDialog';
import { Icon } from '../components/Icon';
import {
  AssignReasonBadge,
  ChannelPill,
  PanelStagePill,
  PaymentPill,
  StatusDot,
} from '../components/Pills';
import { PrintPdfButton } from '../components/PrintPdfButton';
import { useToast } from '../components/Toast';
import { channelLabel } from '../lib/labels';
import { canPrint, markPrinted, slipPath } from '../lib/print-queue';
import { panelFields } from '../lib/panel-fields';
import { orderAmount } from '../lib/order-amount';
import { assignEvidenceText } from '../lib/assign-reason';
import { resolveVehicleLabel, usePanelLabelLookup } from '../lib/vehicle-label';
import type { Order } from '../api/types';

/** Sipariş detay paneli (§3.6 D): 380 px sağ drawer. */
export function OrderDetail({
  order,
  canWrite,
  onClose,
}: {
  order: Order;
  canWrite: boolean;
  onClose: () => void;
}) {
  const vehicles = useVehicles();
  const vehicleLabel = resolveVehicleLabel(order, usePanelLabelLookup());
  const evidence = assignEvidenceText(
    order.assignReason,
    order.assignSource,
    order.vehicle?.plate ?? null,
  );
  const { assign, cancel } = useOrderMutations();
  const hayatPush = useHayatPushMutation();
  const hayatAttempts = useHayatPushAttempts(order.id);
  const tenant = useTenant();
  const me = useMe();
  const isOwner = me.data?.role === 'OWNER';
  const printAllowed = canPrint(me.data?.role);
  const toast = useToast();
  const [showRaw, setShowRaw] = useState(false);
  const [cancelling, setCancelling] = useState(false);
  const [cancelError, setCancelError] = useState<string | null>(null);
  const [hayatConfirmOpen, setHayatConfirmOpen] = useState(false);
  const [reverseOpen, setReverseOpen] = useState(false);
  const reverseLedger = useReverseExternalLedger();

  // HANDLED_EXTERNALLY: Hayat'ta kapandı — atama, iptal ve "Hayat'a gönder" yok; yalnız geri açma.
  const finished = ['DELIVERED', 'LEFT_AT_DOOR', 'CANCELLED', 'HANDLED_EXTERNALLY'].includes(
    order.status,
  );
  const qc = useQueryClient();
  // Geri açma mevcut durum ucuyla yapılır (`POST /api/orders/:id/status`); sunucu ayrıca
  // OWNER/MANAGER dışını reddeder. `canWrite` zaten yalnız OWNER/MANAGER'da doğrudur.
  const reopen = useMutation({
    mutationFn: () => api.post<Order>(`/api/orders/${order.id}/status`, { status: 'CONFIRMED' }),
    onSuccess: async () => {
      await Promise.all([
        qc.invalidateQueries({ queryKey: ['orders'] }),
        qc.invalidateQueries({ queryKey: ['dashboard'] }),
      ]);
    },
  });
  const canReopen = canWrite && order.status === 'HANDLED_EXTERNALLY';

  async function handleReopen(): Promise<void> {
    try {
      await reopen.mutateAsync();
      toast(t('order.reopened'));
    } catch (err) {
      toast(err instanceof ApiError ? err.message : t('error.internal'), 'danger');
    }
  }
  const isHayatOrder = order.source === 'HAYAT_PANEL';
  const hayatMode = tenant.data?.settings.hayatWriteMode ?? null;
  // Y1: düğme DOĞRUDAN yazmaz; sunucudaki onay bekleyen Hayat işini OWNER adına onaylar. Yalnız OWNER ve
  // yalnız sipariş `channelWrite.state === 'awaiting_approval'` iken görünür (eski hayatWriterEnabled
  // bayrağına bağlı DEĞİL). Bitmiş sipariş de olabilir: teslim yazması bitişten sonra onay bekler.
  const pendingHayatAction = order.channelWrite?.action as HayatPushAction | undefined;
  const canHayatPush =
    isOwner &&
    isHayatOrder &&
    order.channelWrite?.state === 'awaiting_approval' &&
    pendingHayatAction !== undefined;

  async function handleAssign(vehicleId: string): Promise<void> {
    try {
      const updated = await assign.mutateAsync({ id: order.id, vehicleId });
      toast(t('panel.assignedToast', { plate: updated.vehicle?.plate ?? '' }));
    } catch (err) {
      toast(err instanceof ApiError ? err.message : t('error.internal'), 'danger');
    }
  }

  async function handleReverseLedger(reasonText: string): Promise<void> {
    try {
      await reverseLedger.mutateAsync({ orderId: order.id, reason: reasonText });
      toast(t('extLedgerUi.reverseDone'));
      setReverseOpen(false);
    } catch (err) {
      toast(err instanceof ApiError ? err.message : t('error.internal'), 'danger');
    }
  }

  /** Gerekçe ConfirmDialog'da en az 3 karakter zorunlu; sunucu şeması değişmedi (reason isteğe bağlı). */
  async function handleCancel(cancelReason: string): Promise<void> {
    setCancelError(null);
    try {
      await cancel.mutateAsync({ id: order.id, reason: cancelReason });
      toast(t('panel.orderCancelled'));
      setCancelling(false);
      onClose();
    } catch (err) {
      setCancelError(err instanceof ApiError ? err.message : t('error.internal'));
    }
  }

  /**
   * Kullanıcının "Hayat'a gönder" düğmesine bastıktan sonra açılan onay. KRİTİK: bu çağrı
   * tetiklemez; yalnız modal açar. Sunucuya yazma `confirmHayatPush` içinde yapılır. Aynı
   * sayfada ikinci kez basılırsa modal zaten açıksa yeniden açılmaz.
   */
  function openHayatConfirm(): void {
    if (!canHayatPush) return;
    setHayatConfirmOpen(true);
  }
  function closeHayatConfirm(): void {
    if (hayatPush.isPending) return;
    setHayatConfirmOpen(false);
  }

  async function confirmHayatPush(): Promise<void> {
    try {
      if (!pendingHayatAction) return;
      const outcome = await hayatPush.mutateAsync({
        orderId: order.id,
        action: pendingHayatAction,
      });
      // Sunucu sonucu insan diline çevrilir — sabit shared paketinde; burada UI sadece taşır.
      const actionText = outcome.action
        ? t(`channelPushAction.${outcome.action}`)
        : t('common.unknown');
      toast(
        outcome.reason
          ? t('hayatPush.ui.resultToastReason', {
              result: t(`channelPushResult.${outcome.result}` as MessageKey),
              reason: t(`hayatPush.reason.${outcome.reason}` as MessageKey),
            })
          : t('hayatPush.ui.resultToast', {
              result: t(`channelPushResult.${outcome.result}`),
              action: actionText,
            }),
        outcome.result === 'SENT' ||
          outcome.result === 'SHADOW_ONLY' ||
          outcome.result === 'PENDING'
          ? 'success'
          : 'danger',
      );
      setHayatConfirmOpen(false);
    } catch (err) {
      toast(err instanceof ApiError ? err.message : t('error.internal'), 'danger');
    }
  }

  return (
    <aside
      style={{
        width: 'var(--drawer-w)',
        flexShrink: 0,
        borderLeft: '1px solid var(--border)',
        background: 'var(--surface)',
        display: 'flex',
        flexDirection: 'column',
        maxHeight: '100%',
      }}
    >
      <header
        style={{
          padding: '20px 24px',
          display: 'flex',
          alignItems: 'center',
          gap: 10,
          borderBottom: '1px solid var(--border)',
        }}
      >
        <div style={{ display: 'flex', flexDirection: 'column', gap: 2, minWidth: 0 }}>
          {order.customerName ? (
            <span
              style={{
                fontWeight: 600,
                fontSize: 17,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
            >
              {order.customerName}
            </span>
          ) : null}
          <span
            className="mono"
            style={{ fontWeight: 600, fontSize: 12.5, color: 'var(--text-secondary)' }}
          >
            {orderCode(order)}
          </span>
        </div>
        <span style={{ flex: 1 }} />
        {printAllowed ? (
          <PrintPdfButton
            path={slipPath(order.id)}
            label={t('panel.print.slipButton')}
            onOpened={() => markPrinted(me.data?.tenantId ?? null, [order.id])}
          />
        ) : null}
        <button
          onClick={onClose}
          aria-label={t('common.close')}
          style={{ color: 'var(--text-secondary)' }}
        >
          <Icon name="x" />
        </button>
      </header>

      <div
        style={{
          flex: 1,
          overflow: 'auto',
          padding: '20px 24px',
          display: 'flex',
          flexDirection: 'column',
          gap: 20,
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
          <ChannelPill channel={order.channel} />
          <StatusDot status={order.status} externalStatusRaw={order.externalStatusRaw} />
          {order.source === 'HAYAT_PANEL' || order.source === 'ABANT_PANEL' ? (
            <PanelStagePill source={order.source} raw={order.externalStatusRaw} />
          ) : null}
          <span style={{ flex: 1 }} />
          <span style={{ fontSize: 13, color: 'var(--text-secondary)' }}>
            {formatDateTime(order.receivedAt)}
          </span>
        </div>

        <div>
          {order.customerPhone && (
            <a href={`tel:${order.customerPhone}`} style={{ fontWeight: 500, fontSize: 14.5 }}>
              {order.customerPhone}
            </a>
          )}
          <div style={{ fontSize: 14.5, lineHeight: 1.5, marginTop: 4 }}>{order.address}</div>
          {/* Not adresin kendisiyse (Abant) tekrar etmez; yalnız FARKLI bilgi taşıyorsa basılır. */}
          {!isRedundantAddressNote(order.addressNote, order.address) && (
            <div style={{ fontSize: 13, color: 'var(--text-secondary)', marginTop: 4 }}>
              {order.addressNote}
            </div>
          )}
          {order.region && (
            <div style={{ fontSize: 12.5, color: 'var(--success)', fontWeight: 600, marginTop: 6 }}>
              {order.region.name}
            </div>
          )}
        </div>

        {(() => {
          const rows = panelFields(order);
          if (rows.length === 0) return null;
          return (
            <div
              data-testid="panel-info-section"
              style={{ background: 'var(--page-bg)', borderRadius: 'var(--r-box)', padding: 16 }}
            >
              <div className="section-label" style={{ marginBottom: 10 }}>
                {t('panelFields.sectionTitle')}
              </div>
              <dl
                style={{
                  display: 'grid',
                  gridTemplateColumns: 'minmax(140px, max-content) 1fr',
                  rowGap: 6,
                  columnGap: 16,
                  margin: 0,
                }}
              >
                {rows.map((row) => (
                  <span
                    key={row.key}
                    data-testid={`panel-field-${row.key}`}
                    style={{ display: 'contents' }}
                  >
                    <dt
                      style={{
                        fontSize: 12.5,
                        color: 'var(--text-secondary)',
                        fontWeight: 500,
                      }}
                    >
                      {row.label}
                    </dt>
                    <dd
                      style={{
                        fontSize: 13.5,
                        fontWeight: 500,
                        margin: 0,
                        whiteSpace: 'pre-wrap',
                      }}
                    >
                      {row.value}
                    </dd>
                  </span>
                ))}
              </dl>
            </div>
          );
        })()}

        <div>
          <div className="section-label" style={{ marginBottom: 10 }}>
            {t('panel.items')}
          </div>
          {order.items.map((item) => (
            <div
              key={item.id}
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                fontSize: 14,
                fontWeight: 500,
                padding: '5px 0',
              }}
            >
              <span>
                {item.qty}× {item.product.name}
              </span>
              <span>{formatMoney(item.lineTotal)}</span>
            </div>
          ))}
          <div
            style={{
              borderTop: '1px solid var(--border)',
              marginTop: 10,
              paddingTop: 10,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: 10,
            }}
          >
            <span style={{ fontWeight: 700, fontSize: 15 }}>{t('panel.total')}</span>
            <span style={{ flex: 1 }} />
            <PaymentPill payment={order.paymentType} locked={order.paymentLocked} />
            <span style={{ fontWeight: 700, fontSize: 18 }}>{formatMoney(orderAmount(order))}</span>
          </div>
          {orderAmount(order) !== order.totalAmount ? (
            <div
              data-testid="panel-amount-note"
              style={{ fontSize: 12, color: 'var(--text-secondary)', textAlign: 'right' }}
            >
              {t('panel.panelAmountNote')}
            </div>
          ) : null}
        </div>

        {order.externalLedger?.state === 'POSTED' ? (
          <div
            data-testid="ledger-posted-badge"
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 10,
              flexWrap: 'wrap',
              background: 'rgba(30,158,106,.12)',
              borderRadius: 'var(--r-box)',
              padding: 12,
            }}
          >
            <span style={{ fontSize: 13, fontWeight: 600, color: 'var(--success)' }}>
              {t('extLedgerUi.postedBadge', {
                date: order.externalLedger.postedAt
                  ? formatDateTime(order.externalLedger.postedAt)
                  : '—',
              })}
            </span>
            {isOwner ? (
              <button
                type="button"
                className="btn"
                data-testid="ledger-reverse-open"
                onClick={() => setReverseOpen(true)}
              >
                {t('extLedgerUi.reverseAction')}
              </button>
            ) : null}
          </div>
        ) : order.externalLedger?.state === 'REVERSED' ? (
          <div
            data-testid="ledger-reversed-badge"
            style={{
              background: 'var(--page-bg)',
              borderRadius: 'var(--r-box)',
              padding: 12,
              fontSize: 13,
              fontWeight: 600,
              color: 'var(--text-secondary)',
            }}
          >
            {t('extLedgerUi.reversedBadge')}
          </div>
        ) : null}

        <div style={{ background: 'var(--page-bg)', borderRadius: 'var(--r-box)', padding: 16 }}>
          <div className="section-label" style={{ marginBottom: 8 }}>
            {t('panel.assignedVehicle')}
          </div>
          {vehicleLabel.kind === 'none' ? (
            <div style={{ fontWeight: 700, fontSize: 15 }}>{t('panel.notAssigned')}</div>
          ) : (
            <div data-testid="order-detail-vehicle">
              <div style={{ fontWeight: 700, fontSize: 15 }}>{vehicleLabel.text}</div>
              {vehicleLabel.needsMapping ? (
                <div style={{ fontSize: 12.5, color: 'var(--text-secondary)', marginTop: 2 }}>
                  {t('orders.vehicleLabel.mapHint')}
                </div>
              ) : null}
            </div>
          )}
          {order.assignReason ? (
            <div
              data-testid="order-detail-assign-reason"
              style={{ marginTop: 8, display: 'flex', flexDirection: 'column', gap: 4 }}
            >
              <span>
                <AssignReasonBadge order={order} />
              </span>
              {evidence ? (
                <span style={{ fontSize: 12.5, color: 'var(--text-secondary)' }}>{evidence}</span>
              ) : null}
            </div>
          ) : null}
          {canWrite && !finished && (
            <select
              className="input"
              style={{ height: 42, marginTop: 10 }}
              value={order.vehicle?.id ?? ''}
              onChange={(e) => {
                if (e.target.value) void handleAssign(e.target.value);
              }}
              aria-label={t('panel.pickVehicle')}
            >
              <option value="">{t('panel.pickVehicle')}</option>
              {(vehicles.data?.items ?? []).map((v) => (
                <option key={v.id} value={v.id}>
                  {v.plate}
                </option>
              ))}
            </select>
          )}
        </div>

        {/* ── Hayat entegrasyonu (paket H): ham durum + düğme + son denemeler.
            Kural: bu blok YALNIZ sipariş HAYAT_PANEL kaynaklıysa görünür. Diğer kanallarda
            göstermek gürültü olur (Hayat'a yazacak bir şey yok). ── */}
        {isHayatOrder ? (
          <div
            data-testid="hayat-panel"
            style={{
              background: 'var(--page-bg)',
              borderRadius: 'var(--r-box)',
              padding: 16,
              display: 'flex',
              flexDirection: 'column',
              gap: 10,
            }}
          >
            <div className="section-label">{t('panelUi.hayatPanelStatusLabel')}</div>
            <div data-testid="hayat-external-status" style={{ fontSize: 14, fontWeight: 600 }}>
              {order.externalStatusRaw?.trim().length
                ? order.externalStatusRaw
                : t('hayatPush.ui.statusEmpty')}
            </div>
            {hayatMode ? (
              <div style={{ fontSize: 12.5, color: 'var(--text-secondary)' }}>
                {t('hayatPush.mobile.modeLabel', {
                  mode: t(`hayatWriteMode.${hayatMode}`),
                })}
              </div>
            ) : null}

            {canHayatPush ? (
              <button
                type="button"
                className="btn"
                style={{ marginTop: 4 }}
                data-testid="hayat-push-open"
                disabled={hayatPush.isPending}
                onClick={openHayatConfirm}
              >
                {t('hayatPush.ui.pushButton')}
              </button>
            ) : null}

            <HayatAttemptsPanel
              attempts={hayatAttempts.data ?? []}
              loading={hayatAttempts.isPending}
            />
          </div>
        ) : null}

        <div>
          <div className="section-label" style={{ marginBottom: 10 }}>
            {t('panel.timeline')}
          </div>
          {order.events.map((event) => (
            <div
              key={event.id}
              style={{
                display: 'grid',
                gridTemplateColumns: '52px 18px 1fr',
                alignItems: 'start',
                gap: 4,
                minHeight: 34,
              }}
            >
              <span style={{ fontSize: 12.5, color: 'var(--text-secondary)' }}>
                {timeLabel(event.createdAt)}
              </span>
              <span
                style={{
                  width: 9,
                  height: 9,
                  borderRadius: '50%',
                  background: 'var(--primary)',
                  marginTop: 4,
                }}
              />
              <span style={{ fontSize: 13.5 }}>
                <strong style={{ fontWeight: 600 }}>{eventLabel(event.type)}</strong>
                {event.detail && (
                  <span style={{ color: 'var(--text-secondary)' }}>
                    {' · '}
                    {eventDetail(event.type, event.detail)}
                  </span>
                )}
              </span>
            </div>
          ))}
        </div>

        {order.cancelReason && (
          <div
            style={{
              background: 'var(--bg-danger-soft)',
              borderRadius: 'var(--r-menu)',
              padding: 12,
              fontSize: 13.5,
            }}
          >
            {t('panel.cancelReason')}: {order.cancelReason}
          </div>
        )}

        {order.rawPayload !== null && order.rawPayload !== undefined && (
          <div>
            <button
              className="btn"
              style={{ color: 'var(--primary)', fontSize: 13, padding: 0 }}
              onClick={() => setShowRaw((v) => !v)}
            >
              {t('order.rawRecord')} {showRaw ? '−' : '+'}
            </button>
            {showRaw && (
              <pre
                className="mono"
                style={{
                  marginTop: 8,
                  background: 'var(--page-bg)',
                  borderRadius: 'var(--r-menu)',
                  padding: 12,
                  fontSize: 11.5,
                  overflow: 'auto',
                  maxHeight: 220,
                }}
              >
                {JSON.stringify(order.rawPayload, null, 2)}
              </pre>
            )}
          </div>
        )}
      </div>

      {canWrite && !finished && (
        <footer style={{ padding: '16px 24px', borderTop: '1px solid var(--border)' }}>
          <button className="btn btn-danger" onClick={() => setCancelling(true)}>
            {t('panel.cancelOrder')}
          </button>
          {cancelling && (
            <ConfirmDialog
              title={t('order.cancel.title')}
              body={t('order.cancel.panelBody')}
              confirmLabel={t('panel.cancelOrder')}
              tone="danger"
              pending={cancel.isPending}
              error={cancelError}
              requireReason={{ label: t('panel.cancelReason'), minLength: 3, maxLength: 300 }}
              onConfirm={(cancelReason) => void handleCancel(cancelReason ?? '')}
              onCancel={() => {
                setCancelling(false);
                setCancelError(null);
              }}
            />
          )}
        </footer>
      )}

      {canReopen && (
        <footer style={{ padding: '16px 24px', borderTop: '1px solid var(--border)' }}>
          <button
            className="btn btn-primary"
            data-testid="order-reopen"
            disabled={reopen.isPending}
            onClick={() => void handleReopen()}
          >
            {t('order.reopenAction')}
          </button>
        </footer>
      )}

      {hayatConfirmOpen ? (
        <HayatPushConfirmModal
          order={order}
          action={pendingHayatAction}
          pending={hayatPush.isPending}
          onCancel={closeHayatConfirm}
          onConfirm={() => void confirmHayatPush()}
        />
      ) : null}

      {reverseOpen ? (
        <ReverseLedgerModal
          pending={reverseLedger.isPending}
          onCancel={() => setReverseOpen(false)}
          onConfirm={(reasonText) => void handleReverseLedger(reasonText)}
        />
      ) : null}
    </aside>
  );
}

/**
 * Ters kayıt onayı (paket ExternalLedgerCard'ın sipariş detayındaki karşılığı). Gerekçe metni
 * en az 10 karakter olmadan gönder kapalıdır — sunucu da aynı sınırı zorunlu kılar.
 */
function ReverseLedgerModal({
  pending,
  onCancel,
  onConfirm,
}: {
  pending: boolean;
  onCancel: () => void;
  onConfirm: (reason: string) => void;
}) {
  const [reasonText, setReasonText] = useState('');
  const canSubmit = reasonText.trim().length >= 10 && !pending;
  return (
    <div
      role="dialog"
      aria-modal="true"
      className="modal-overlay"
      data-testid="ledger-reverse-confirm"
      onClick={(e) => {
        if (e.target === e.currentTarget && !pending) onCancel();
      }}
    >
      <div
        className="card modal-card"
        style={{ '--modal-w': '440px' } as React.CSSProperties}
        onClick={(e) => e.stopPropagation()}
      >
        <div style={{ padding: 20, display: 'flex', flexDirection: 'column', gap: 12 }}>
          <strong style={{ fontSize: 16 }}>{t('extLedgerUi.reverseAction')}</strong>
          <p style={{ margin: 0, fontSize: 13.5, lineHeight: 1.55 }}>
            {t('extLedgerUi.reverseConfirm')}
          </p>
          <label style={{ display: 'flex', flexDirection: 'column', gap: 6, fontSize: 13 }}>
            {t('extLedgerUi.reverseReasonLabel')}
            <textarea
              className="input"
              data-testid="ledger-reverse-reason"
              value={reasonText}
              onChange={(e) => setReasonText(e.target.value)}
              placeholder={t('extLedgerUi.reverseReasonPlaceholder')}
              rows={3}
            />
            <span style={{ fontSize: 11.5, color: 'var(--text-secondary)' }}>
              {t('extLedgerUi.reasonNoPersonal')}
            </span>
          </label>
          <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
            <button
              type="button"
              className="btn"
              data-testid="ledger-reverse-cancel"
              onClick={onCancel}
              disabled={pending}
            >
              {t('common.cancel')}
            </button>
            <button
              type="button"
              className="btn btn-danger"
              data-testid="ledger-reverse-submit"
              disabled={!canSubmit}
              onClick={() => onConfirm(reasonText.trim())}
            >
              {t('extLedgerUi.reverseAction')}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

/**
 * Hayat'a yazma onayı. İki kez basılsın diye değil: KULLANICI KURALI (paket H).
 * "Benden habersiz Hayat'a yazma olmamalı" — düğmeye bassa bile bu pencereyi AÇMADAN
 * sunucuya gitmez. Modal'da "iptal" etkili; arka plana tıklanırsa kapanır.
 */
function HayatPushConfirmModal({
  order,
  action,
  pending,
  onCancel,
  onConfirm,
}: {
  order: Order;
  /** Sunucunun onay bekleyen işinin eylemi (`channelWrite.action`); modal yalnız bunu gösterir. */
  action: HayatPushAction | undefined;
  pending: boolean;
  onCancel: () => void;
  onConfirm: () => void;
}) {
  const actionText = action ? t(`channelPushAction.${action}`) : t('common.unknown');
  const bodyKey = 'hayatPush.ui.confirmBody';
  return (
    <div
      role="dialog"
      aria-modal="true"
      className="modal-overlay"
      data-testid="hayat-push-confirm"
      onClick={(e) => {
        if (e.target === e.currentTarget) onCancel();
      }}
    >
      <div
        className="card modal-card"
        style={{ '--modal-w': '440px' } as React.CSSProperties}
        onClick={(e) => e.stopPropagation()}
      >
        <div style={{ padding: 20, display: 'flex', flexDirection: 'column', gap: 12 }}>
          <strong style={{ fontSize: 16 }}>{t('hayatPush.ui.confirmTitle')}</strong>
          <p style={{ margin: 0, fontSize: 14, lineHeight: 1.55 }}>
            {t(bodyKey, { action: actionText })}
          </p>
          {order.externalStatusRaw ? (
            <p
              style={{
                margin: 0,
                fontSize: 12.5,
                color: 'var(--text-secondary)',
                background: 'var(--page-bg)',
                padding: 10,
                borderRadius: 'var(--r-row)',
              }}
            >
              {t('hayatPush.ui.statusLabel')}: {order.externalStatusRaw}
            </p>
          ) : null}
          <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 4 }}>
            <button
              type="button"
              className="btn"
              data-testid="hayat-push-cancel"
              onClick={onCancel}
              disabled={pending}
            >
              {t('common.cancel')}
            </button>
            <button
              type="button"
              className="btn btn-primary"
              data-testid="hayat-push-confirm-btn"
              onClick={onConfirm}
              disabled={pending}
            >
              {t('hayatPush.ui.pushButton')}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

/** Son Hayat denemeleri listesi. Hata varsa kırmızı, başarı yeşil gösterilir. */
/**
 * Deneme kodunun Türkçe karşılığı: önce `channelWrite.error.*`, yoksa `hayatPush.reason.*`, o da yoksa
 * genel "bilinmeyen kod" metni. Ham makine kodu kullanıcıya ASLA gösterilmez (eski satırlarda `reason:` öneki olabilir).
 */
function attemptCodeLabel(code: string): string {
  const bare = code.startsWith('reason:') ? code.slice('reason:'.length) : code;
  const primary = `channelWrite.error.${bare}`;
  if (primary in tr) return t(primary as MessageKey);
  const secondary = `hayatPush.reason.${bare}`;
  if (secondary in tr) return t(secondary as MessageKey);
  return t('hayatPush.ui.attemptCodeUnknown');
}

function HayatAttemptsPanel({
  attempts,
  loading,
}: {
  attempts: {
    id: string;
    action: 'CONFIRM_VEHICLE' | 'SEND_TO_VEHICLE' | 'MARK_DELIVERED' | 'CHANGE_VEHICLE' | 'CANCEL';
    mode: 'SHADOW' | 'CONFIRM' | 'AUTO';
    trigger: 'SCHEDULER' | 'BUTTON';
    result:
      | 'SENT'
      | 'SKIPPED_ALREADY_DONE'
      | 'SHADOW_ONLY'
      | 'BLOCKED'
      | 'FAILED'
      | 'UNCERTAIN'
      | 'PENDING';
    error: string | null;
    httpStatus?: number | null;
    createdAt: string;
  }[];
  loading: boolean;
}) {
  return (
    <div
      style={{ marginTop: 6, borderTop: '1px dashed var(--border)', paddingTop: 10 }}
      data-testid="hayat-attempts"
    >
      <div className="section-label" style={{ fontSize: 12, marginBottom: 6 }}>
        {t('hayatPush.ui.attemptsTitle')}
      </div>
      {loading ? (
        <span style={{ fontSize: 12.5, color: 'var(--text-secondary)' }}>
          {t('common.loading')}
        </span>
      ) : attempts.length === 0 ? (
        <span style={{ fontSize: 12.5, color: 'var(--text-secondary)' }}>
          {t('hayatPush.ui.attemptsEmpty')}
        </span>
      ) : (
        <ul
          style={{
            listStyle: 'none',
            margin: 0,
            padding: 0,
            display: 'flex',
            flexDirection: 'column',
            gap: 6,
          }}
        >
          {attempts.slice(0, 5).map((a) => {
            const isFailure = ['FAILED', 'BLOCKED'].includes(a.result);
            return (
              <li
                key={a.id}
                style={{
                  fontSize: 12.5,
                  display: 'flex',
                  justifyContent: 'space-between',
                  gap: 8,
                  alignItems: 'baseline',
                }}
              >
                <span className="mono" style={{ fontSize: 11.5, color: 'var(--text-secondary)' }}>
                  {timeLabel(a.createdAt)}
                </span>
                <span style={{ flex: 1, fontWeight: 500 }}>
                  {t(`channelPushAction.${a.action}`)}
                </span>
                <span
                  style={{
                    fontWeight: 600,
                    color: isFailure ? 'var(--danger)' : 'var(--success)',
                  }}
                >
                  {t(`channelPushResult.${a.result}`)}
                </span>
                {a.error ? (
                  <span
                    className="mono"
                    style={{ fontSize: 11, color: 'var(--text-secondary)' }}
                    title={t('hayatPush.ui.attemptsColCode')}
                    data-testid="hayat-attempt-code"
                  >
                    {attemptCodeLabel(a.error)}
                  </span>
                ) : null}
                {a.httpStatus ? (
                  <span
                    className="mono"
                    style={{ fontSize: 11, color: 'var(--text-secondary)' }}
                    data-testid="hayat-attempt-http"
                  >
                    {t('hayatPush.ui.attemptsColHttp')} {a.httpStatus}
                  </span>
                ) : null}
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}

/**
 * Sipariş kodu: kanal adı + kimliğin son 6 karakteri ("Trendyol #4F2A1C"). Kaynak enum'u
 * (HAYAT_PANEL → "HA") kullanıcıya sızmaz. Kimlik cuid olduğu için son 6 karakter kullanılır.
 * Düz `toUpperCase()` kullanılır — `toLocaleUpperCase('tr-TR')` bir cuid gibi OPAK kimlikte
 * yanlış: içinde 'i' geçerse noktalı büyük 'İ' (U+0130) üretir, ASCII arama/karşılaştırma bunu
 * bulamaz. `tr-TR` büyütme yalnız dilbilimsel Türkçe METİN için doğrudur (bkz. CLAUDE.md İ/I testi,
 * normalizeEmail ile aynı ders — Faz 1 denetimi U9).
 */
function orderCode(order: Order): string {
  return `${channelLabel(order.channel)} #${order.id.slice(-6).toUpperCase()}`;
}

/** `REGION_MATCHED` ayrıntısı makine kodudur ("TEXT:ESTIMATED"); okunur Türkçeye çevrilir. */
function eventDetail(type: string, detail: string): string {
  if (type !== 'REGION_MATCHED') return detail;
  const [by, conf] = detail.includes(':') ? detail.split(':') : [null, detail];
  const confLabel =
    conf === 'EXACT' || conf === 'ESTIMATED' || conf === 'NONE' ? t(`regionMatch.${conf}`) : detail;
  const byLabel =
    by === 'STREET' || by === 'TEXT' || by === 'COORDINATE' ? t(`regionMatch.by.${by}`) : null;
  return byLabel ? `${byLabel} · ${confLabel}` : confLabel;
}

function eventLabel(type: string): string {
  return t(`orderEvent.${type}` as Parameters<typeof t>[0]);
}

function timeLabel(iso: string): string {
  return new Intl.DateTimeFormat('tr-TR', {
    timeZone: 'Europe/Istanbul',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  }).format(new Date(iso));
}
